import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls, selectCls, statusBadge } from '../../utils/operations';

const LANGS = ['pt', 'en', 'de', 'fr'];
const STATUSES = ['draft', 'published', 'archived'];
const ICONS = ['Trees', 'Landmark', 'Waves', 'Coffee', 'UtensilsCrossed', 'Mountain', 'Bird', 'Users', 'Heart', 'MapPin', 'Sailboat', 'Sparkles', 'Star', 'Compass'];

const EMPTY = {
  slug: '', name: { pt: '', en: '', de: '', fr: '' }, description: { pt: '', en: '', de: '', fr: '' },
  icon: 'Sparkles', image: '', order: 99, experience_slugs: [], status: 'draft',
};

const toForm = (c) => (c ? {
  ...EMPTY, ...c,
  name: { ...EMPTY.name, ...c.name },
  description: { ...EMPTY.description, ...(c.description || {}) },
} : EMPTY);

export const AdminInspirations = () => {
  const [items, setItems] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setName = (l, v) => setForm((f) => ({ ...f, name: { ...f.name, [l]: v } }));
  const setDesc = (l, v) => setForm((f) => ({ ...f, description: { ...f.description, [l]: v } }));

  const load = async () => {
    try {
      setItems(await apiFetch('/admin/inspiration-categories'));
    } catch (e) {
      toast.error('Erro ao carregar', { description: String(e.message) });
    }
  };
  useEffect(() => {
    load();
    apiFetch('/admin/experiences').then((d) => setExperiences(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiFetch('/upload', { method: 'POST', body: fd });
      set('image', res.url);
      toast.success('Imagem carregada');
    } catch (e) {
      toast.error('Upload falhou', { description: String(e.message) });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editId) {
        await apiFetch(`/admin/inspiration-categories/${editId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      } else {
        await apiFetch('/admin/inspiration-categories', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
      toast.success(editId ? 'Categoria atualizada' : 'Categoria criada');
      setForm(null); setEditId(null);
      await load();
    } catch (err) {
      toast.error('Não foi possível guardar', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminar a categoria "${c.name?.pt || c.slug}"?`)) return;
    try {
      await apiFetch(`/admin/inspiration-categories/${c.id}`, { method: 'DELETE' });
      toast.success('Categoria eliminada');
      await load();
    } catch (err) {
      toast.error('Não foi possível eliminar', { description: String(err.message) });
    }
  };

  const toggleExp = (slug) => set('experience_slugs', form.experience_slugs.includes(slug) ? form.experience_slugs.filter((s) => s !== slug) : [...form.experience_slugs, slug]);

  return (
    <div data-testid="admin-inspirations" className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-eco-ink/55">Taxonomia "Find Your Inspiration" — só categorias com experiências reais aparecem no site. Liga experiências sem duplicar conteúdo.</p>
        <button data-testid="inspiration-new" onClick={() => { setForm(EMPTY); setEditId(null); }} className="eco-btn-primary shrink-0"><Plus size={14} /> Nova categoria</button>
      </div>

      <div className="mt-5 space-y-3">
        {items.map((c) => (
          <article key={c.id} data-testid={`admin-inspiration-${c.slug}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-eco-ink/10 bg-white px-5 py-4">
            <span className="eco-number grid h-9 w-9 place-items-center rounded-lg bg-eco-cream text-xs font-bold text-eco-green">{c.order}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-eco-ink">{c.name?.pt || c.slug}</p>
              <p className="mt-0.5 text-xs text-eco-ink/55">{c.slug} · {c.experience_slugs.length} {c.experience_slugs.length === 1 ? 'experiência' : 'experiências'} · {c.icon}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(c.status)}`}>{c.status}</span>
            <button data-testid={`inspiration-edit-${c.slug}`} onClick={() => { setForm(toForm(c)); setEditId(c.id); }} className="eco-btn-secondary !min-h-9 px-3 text-xs"><Pencil size={13} /> Editar</button>
            <button data-testid={`inspiration-delete-${c.slug}`} onClick={() => remove(c)} className="grid min-h-9 w-9 place-items-center rounded-lg border border-red-200 text-red-500 transition-colors hover:bg-red-50"><Trash2 size={14} /></button>
          </article>
        ))}
        {items.length === 0 && <p data-testid="inspirations-empty" className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-10 text-center text-sm text-eco-ink/50">Sem categorias de inspiração.</p>}
      </div>

      {form && (
        <form data-testid="inspiration-form" onSubmit={save} className="mt-6 space-y-5 rounded-xl border border-eco-ink/10 bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-eco-ink">{editId ? 'Editar categoria' : 'Nova categoria'}</h3>
            <button type="button" data-testid="inspiration-form-cancel" onClick={() => { setForm(null); setEditId(null); }} className="text-eco-ink/40 hover:text-eco-ink"><X size={18} /></button>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <label><span className="eco-field-label mb-1 block">Slug *</span><input required disabled={!!editId} data-testid="insp-form-slug" value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} placeholder="nature-adventure" className={inputCls} /></label>
            <label><span className="eco-field-label mb-1 block">Ícone</span><select data-testid="insp-form-icon" value={form.icon} onChange={(e) => set('icon', e.target.value)} className={selectCls}>{ICONS.map((i) => <option key={i} value={i}>{i}</option>)}</select></label>
            <label><span className="eco-field-label mb-1 block">Ordem</span><input type="number" data-testid="insp-form-order" value={form.order} onChange={(e) => set('order', e.target.value)} className={inputCls} /></label>
            <label><span className="eco-field-label mb-1 block">Estado</span><select data-testid="insp-form-status" value={form.status} onChange={(e) => set('status', e.target.value)} className={selectCls}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {LANGS.map((l) => (
              <label key={l}><span className="eco-field-label mb-1 block">Nome ({l.toUpperCase()}{l === 'pt' ? ' *' : ''})</span><input required={l === 'pt'} data-testid={`insp-form-name-${l}`} value={form.name[l]} onChange={(e) => setName(l, e.target.value)} className={inputCls} /></label>
            ))}
            {LANGS.map((l) => (
              <label key={l}><span className="eco-field-label mb-1 block">Descrição ({l.toUpperCase()})</span><input data-testid={`insp-form-desc-${l}`} value={form.description[l]} onChange={(e) => setDesc(l, e.target.value)} className={inputCls} /></label>
            ))}
          </div>

          <div>
            <p className="eco-field-label mb-1">Imagem</p>
            <div className="flex items-center gap-3">
              {form.image && <img src={form.image} alt="" className="h-16 w-28 rounded-lg object-cover" data-testid="insp-form-image-preview" />}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="insp-form-image-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
              <button type="button" data-testid="insp-form-image-upload" onClick={() => fileRef.current?.click()} className="eco-btn-secondary !min-h-10 px-4 text-xs"><ImagePlus size={14} /> Carregar imagem</button>
              {form.image && <button type="button" onClick={() => set('image', '')} className="text-red-400" data-testid="insp-form-image-clear"><X size={15} /></button>}
            </div>
          </div>

          <div>
            <p className="eco-field-label mb-2">Experiências ligadas ({form.experience_slugs.length})</p>
            <div className="grid max-h-56 gap-1.5 overflow-y-auto rounded-xl bg-eco-cream p-3 sm:grid-cols-2" data-testid="insp-form-experiences">
              {experiences.map((e) => (
                <label key={e.slug} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs font-medium text-eco-ink/75">
                  <input type="checkbox" data-testid={`insp-exp-${e.slug}`} checked={form.experience_slugs.includes(e.slug)} onChange={() => toggleExp(e.slug)} className="h-4 w-4 accent-eco-green" />
                  <span className="truncate">{e.name} <span className="text-eco-ink/40">· {e.island}</span></span>
                </label>
              ))}
            </div>
          </div>

          <button type="submit" data-testid="insp-form-save" disabled={busy} className="eco-btn-primary disabled:opacity-60">
            {busy && <Loader2 size={15} className="animate-spin" />} {editId ? 'Guardar alterações' : 'Criar categoria'}
          </button>
        </form>
      )}
    </div>
  );
};
